import React, { useState } from 'react'
import useSWR from 'swr'
import { fetcher, apiRequest } from '../utils/api'
import { Receipt, DollarSign, Clock, AlertCircle, RotateCcw, XCircle } from 'lucide-react'
import PageHeader from '../components/admin/PageHeader'
import StatCard from '../components/admin/StatCard'
import DataTable from '../components/admin/DataTable'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'

export default function Invoices() {
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const [actionLoading, setActionLoading] = useState(null)

  const { data, isLoading, mutate } = useSWR(`/api/admin/invoices?page=${page}&limit=20&status=${status}`, fetcher)
  const { data: summary } = useSWR('/api/admin/invoices/summary', fetcher)

  const handleRefund = async (invoiceId) => {
    if (!confirm('Are you sure you want to refund this invoice?')) return
    setActionLoading(invoiceId)
    try { await apiRequest(`/api/admin/invoices/${invoiceId}/refund`, { method: 'POST' }); mutate() }
    catch (err) { alert(err.message) } finally { setActionLoading(null) }
  }

  const handleCancel = async (invoiceId) => {
    if (!confirm('Are you sure you want to cancel this invoice?')) return
    setActionLoading(invoiceId)
    try { await apiRequest(`/api/admin/invoices/${invoiceId}/cancel`, { method: 'POST' }); mutate() }
    catch (err) { alert(err.message) } finally { setActionLoading(null) }
  }

  const formatCurrency = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)
  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'

  const statusBadge = (s) => {
    const map = {
      paid: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-amber-100 text-amber-700',
      overdue: 'bg-rose-100 text-rose-700',
      cancelled: 'bg-gray-100 text-gray-600',
      refunded: 'bg-purple-100 text-purple-700',
    }
    return <Badge className={map[s] || 'bg-gray-100 text-gray-600'}>{s || 'unknown'}</Badge>
  }

  const columns = [
    {
      key: 'invoice', header: 'Invoice', accessor: (row) => row.invoice_id,
      render: (row) => (<div><p className="font-medium text-gray-900 text-sm">{row.invoice_id?.slice(0, 8)}...</p><p className="text-xs text-gray-500">{row.description || 'Invoice'}</p></div>),
    },
    { key: 'team', header: 'Team', accessor: 'team_name', render: (row) => <span className="font-medium text-gray-900">{row.team_name}</span> },
    {
      key: 'user', header: 'User', accessor: (row) => row.user_name,
      render: (row) => (<div><p className="font-medium text-gray-900">{row.user_name}</p><p className="text-xs text-gray-500">{row.user_email}</p></div>),
    },
    { key: 'amount', header: 'Amount', accessor: (row) => row.amount, render: (row) => <span className="font-bold text-gray-900">{formatCurrency(row.amount || 0)}</span> },
    { key: 'status', header: 'Status', accessor: 'status', render: (row) => statusBadge(row.status) },
    { key: 'date', header: 'Date', accessor: 'created_at', render: (row) => <span className="text-sm text-gray-500">{formatDate(row.created_at)}</span> },
    {
      key: 'actions', header: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.status === 'paid' && <Button variant="ghost" size="icon" className="text-purple-600" onClick={() => handleRefund(row.invoice_id)} disabled={actionLoading === row.invoice_id} title="Refund"><RotateCcw className="w-4 h-4" /></Button>}
          {row.status === 'pending' && <Button variant="ghost" size="icon" className="text-rose-500" onClick={() => handleCancel(row.invoice_id)} disabled={actionLoading === row.invoice_id} title="Cancel"><XCircle className="w-4 h-4" /></Button>}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Invoices" description="View and manage all invoices" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard title="Total Revenue" value={formatCurrency(summary?.revenue?.total || 0)} icon={DollarSign} color="indigo" />
        <StatCard title="This Month" value={formatCurrency(summary?.revenue?.this_month || 0)} icon={Receipt} color="emerald" />
        <StatCard title="Pending" value={formatCurrency(summary?.revenue?.pending || 0)} icon={Clock} color="amber" />
        <StatCard title="Overdue" value={summary?.status_counts?.overdue || 0} icon={AlertCircle} color="red" />
      </div>

      <div className="flex gap-4">
        <Select value={status || 'all'} onValueChange={(val) => { setStatus(val === 'all' ? '' : val); setPage(1) }}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div></div>
      ) : (
        <DataTable columns={columns} data={data?.invoices || []} sortable emptyMessage="No invoices found" />
      )}

      {data?.pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Page {page} of {data.pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page === data.pages}>Next</Button>
          </div>
        </div>
      )}
    </div>
  )
}
